import { DefaultOptionType } from "antd5/es/select";
import _ from "lodash";
import qs from "qs";

import { NoticeFilters } from "components/notices/utils";
import {
  SearchFrameworksRequest,
  SearchFrameworksRequest_StartDate as DatesFilter,
} from "lib/generated/app-service-gql/graphql";
import { RelevanceScoreEnum } from "lib/generated/saved-views/viewConfiguration";
import { encodeCompressed, parseBooleanValue, parseNumberValue } from "lib/hooks/useURLState";
import {
  AwardType as FrameworkAwardType,
  BuyerParticipantType,
  FrameworkStagesSearch,
  SearchRequestAwardType,
  SearchRequestProcedureType as ProcedureType,
  SearchRequestSortOrder,
} from "lib/types/graphQLEnums";
import { ColumnSetting, CountryRegion } from "lib/types/models";
import { isDefined } from "lib/utils";
import {
  getMinRelevanceScore,
  mapSignalsToViewSignals,
  SignalsFilter,
} from "lib/utils/signalUtils";

type SortField =
  | "signalScore"
  | "title"
  | "procedureType"
  | "stage"
  | "awardType"
  | "value"
  | "suppliers"
  | "startDate"
  | "endDate"
  | "closeDate"
  | "callOffs";

const validSortfields: SortField[] = [
  "signalScore",
  "title",
  "procedureType",
  "stage",
  "awardType",
  "value",
  "suppliers",
  "startDate",
  "endDate",
  "closeDate",
  "callOffs",
];

export function isValidSortField(field?: string): SortField | undefined {
  if (field && validSortfields.includes(field as SortField)) {
    return field as SortField;
  }
  return undefined;
}

export type FrameworkFilters = {
  signals: string[];
  signalScore: RelevanceScoreEnum[];
  stage?: FrameworkStagesSearch[];
  procedureType?: string[];
  languages?: string[];
  buyerParticipantType?: BuyerParticipantType;
  awardType?: string[];
  buyerIds?: string[];
  buyerListIds?: string[];
  buyerCategories?: string[];
  buyerCountryRegions?: CountryRegion;
  supplierIds?: string[];
  searchText: string;
  value: { from?: number; to?: number; hideNulls?: boolean };
  closeDate?: DatesFilter;
  endDate?: DatesFilter;
  startDate?: DatesFilter;
  cpvDimensions?: string[];
  cpvCodes?: string[];
  keywords?: string[];
  excludeKeywords?: string[];
  sort?: {
    field: SortField;
    order: "ASC" | "DESC";
  };
};

type Pagination = {
  pageSize: number;
  current: number;
};

function formatSignals(signals: string[]) {
  const signalViewConfig: SignalsFilter = mapSignalsToViewSignals(signals);
  return {
    categories: signalViewConfig.categories ? signalViewConfig.categories : [],
    signalIds: signalViewConfig.ids ? signalViewConfig.ids : [],
  };
}

export function convertFrameworkFiltersToRequest(
  filters: FrameworkFilters,
  pagination?: Pagination,
): SearchFrameworksRequest {
  return {
    signals: !_.isEmpty(filters.signals) ? formatSignals(filters.signals) : undefined,
    searchText: filters.searchText,
    procedureTypes: filters.procedureType?.map((type) => type as ProcedureType).filter(isDefined),
    // TODO: From a frontend perspective, how do we get the supported languages - Or should this be entirely backend resolved?
    languages: filters.languages,
    // Tests fail without casting, empty string is passed in as a buyerParticipantType
    buyerParticipantType: filters.buyerParticipantType
      ? (filters.buyerParticipantType as BuyerParticipantType)
      : undefined,
    buyerListGuids: filters.buyerListIds,
    buyerCountryCodes: filters.buyerCountryRegions?.countries,
    buyerRegionGuids: filters.buyerCountryRegions?.regions,
    buyerCategoryGuids: filters.buyerCategories,
    stages: filters.stage,
    buyerGuids: filters.buyerIds,
    supplierGuids: filters.supplierIds,
    startDate: filters.startDate,
    endDate: filters.endDate,
    // TODO: Add UI for last updated date filtering
    lastUpdatedDate: undefined,
    awardTypes: filters.awardType?.map((type) => type as SearchRequestAwardType).filter(isDefined),
    closeDate: filters.closeDate,
    value: {
      from: filters.value?.from,
      to: filters.value?.to,
      hideNulls: filters.value?.hideNulls,
    },
    cpvCodes: filters.cpvCodes,
    keywords: filters.keywords,
    excludeKeywords: filters.excludeKeywords,
    minScore: getMinRelevanceScore(filters.signalScore ?? []),
    sortOrder: (filters.sort?.order as SearchRequestSortOrder) || SearchRequestSortOrder.Asc,
    sort: filters.sort?.field || "name",
    limit: pagination ? pagination.pageSize : 10,
    page: pagination?.current ?? 1,
  };
}

export const DEFAULT_FILTERS: FrameworkFilters = {
  signals: [],
  signalScore: [],
  searchText: "",
  value: {},
  closeDate: {},
  startDate: {},
  endDate: {},
  sort: {
    field: "signalScore",
    order: "DESC",
  },
};

export const DEFAULT_PAGINATION = { current: 1, pageSize: 10 };

export const PROCEDURE_TYPE_LABEL: Record<ProcedureType, string> = {
  [ProcedureType.Framework]: "Framework",
  [ProcedureType.DynamicPurchasingSystem]: "DPS",
};

export const PROCEDURE_TYPE_OPTIONS: DefaultOptionType[] = [
  { label: "Framework", value: ProcedureType.Framework },
  {
    label: "Dynamic purchasing system (DPS)",
    value: ProcedureType.DynamicPurchasingSystem,
  },
];

export const AWARD_TYPE_LABEL: Record<FrameworkAwardType, string> = {
  [FrameworkAwardType.CompetitionOnly]: "Competition Only",
  [FrameworkAwardType.CompetitionAndDirect]: "Competition & Direct",
  [FrameworkAwardType.DirectAward]: "Direct Only",
  [FrameworkAwardType.Other]: "Other",
};

export const AWARD_TYPE_OPTIONS: DefaultOptionType[] = [
  //   { label: "Call off", value: FrameworkAwardType.CallOff },
  {
    label: AWARD_TYPE_LABEL[FrameworkAwardType.CompetitionAndDirect],
    value: FrameworkAwardType.CompetitionAndDirect,
  },
  {
    label: AWARD_TYPE_LABEL[FrameworkAwardType.CompetitionOnly],
    value: FrameworkAwardType.CompetitionOnly,
  },
  {
    label: AWARD_TYPE_LABEL[FrameworkAwardType.DirectAward],
    value: FrameworkAwardType.DirectAward,
  },
  { label: AWARD_TYPE_LABEL[FrameworkAwardType.Other], value: FrameworkAwardType.Other },
];

export const BUYER_PARTICIPANT_OPTIONS = [
  { label: "All buyers", value: "" },
  { label: "Framework providers only", value: BuyerParticipantType.ProvidersOnly },
  {
    label: "Contracting authority only",
    value: BuyerParticipantType.ContractingAuthorityOnly,
  },
];

export function parseFrameworksUrlState(
  state: unknown,
  defaultFilters = DEFAULT_FILTERS,
): FrameworkFilters {
  if (typeof state !== "object" || state === null) {
    return defaultFilters;
  }

  const filters = state as FrameworkFilters;

  return {
    ...filters,
    value: {
      from: parseNumberValue(filters.value?.from),
      to: parseNumberValue(filters.value?.to),
      hideNulls: parseBooleanValue(filters.value?.hideNulls),
    },
    startDate: {
      ...filters.startDate,
      hideNulls: parseBooleanValue(filters.startDate?.hideNulls),
    },
    endDate: {
      ...filters.endDate,
      hideNulls: parseBooleanValue(filters.endDate?.hideNulls),
    },
    closeDate: {
      ...filters.closeDate,
      hideNulls: parseBooleanValue(filters.closeDate?.hideNulls),
    },
    signals: filters.signals ?? [], // Should always have a default value
    signalScore: filters.signalScore ?? [], // Should always have a default value
  };
}

export function frameworksUrl(filters: FrameworkFilters): string {
  const params = qs.stringify(
    { frameworkFilters: encodeCompressed(filters) },
    { arrayFormat: "brackets", addQueryPrefix: true },
  );
  return `/frameworks${params}`;
}

/**
 * Structured `{ to, search }` target for a framework's details page, with active framework filters
 * carried over in `search`.
 */
export function frameworkDetailsTarget(
  id: string,
  filters?: FrameworkFilters,
): { to: string; search?: Record<string, unknown> } {
  return {
    to: `/frameworks/${id}`,
    search: filters ? { frameworkFilters: encodeCompressed(filters) } : undefined,
  };
}

export function frameworkCallOffsUrl(id: string, filters?: NoticeFilters): string {
  if (filters) {
    const params = qs.stringify(
      { noticeFilters: encodeCompressed(filters) },
      { arrayFormat: "brackets", addQueryPrefix: true },
    );
    return `/frameworks/${id}/call-offs${params}`;
  }

  return `/frameworks/${id}/call-offs`;
}

/**
 * Converts notice filters to framework filters. We may need this when we are
 * going from notices to frameworks and want to remember what filters were
 * applied. In highlighting for example
 * @param noticefilters
 */
export function convertNoticeFiltersToFrameworkFilters(
  noticefilters?: NoticeFilters,
): FrameworkFilters | undefined {
  if (!noticefilters) {
    return undefined;
  }
  return {
    ...DEFAULT_FILTERS,
    signals: noticefilters.signals,
    signalScore: noticefilters.relevanceScore,
    searchText: noticefilters.text,
    value: noticefilters.value,
    closeDate: noticefilters.closeDate,
    endDate: noticefilters.expiryDate,
    supplierIds: noticefilters.supplierGuids,
    keywords: noticefilters.keywords,
    excludeKeywords: noticefilters.excludeKeywords,
    cpvCodes: noticefilters.cpvCodes,
    buyerIds: noticefilters.buyers,
    buyerListIds: noticefilters.buyerListIds,
    buyerCategories: noticefilters.buyerCategories,
    buyerCountryRegions: noticefilters.buyerCountryRegions,
  };
}

export type FrameworkColumns =
  | "signalScore"
  | "title"
  | "procedureType"
  | "awardType"
  | "stage"
  | "value"
  | "suppliers"
  | "startDate"
  | "endDate"
  | "closeDate"
  | "callOffs"
  | "signals";

export const DEFAULT_FRAMEWORK_COLUMNS: ColumnSetting<FrameworkColumns>[] = [
  { title: "Signal score", field: "signalScore" },
  { title: "Framework & Provider", field: "title", disabled: true },
  { title: "Framework type", field: "procedureType" },
  { title: "Award type", field: "awardType" },
  { title: "Stage", field: "stage" },
  { title: "Value", field: "value" },
  { title: "Suppliers", field: "suppliers" },
  { title: "Start date", field: "startDate" },
  { title: "End date", field: "endDate" },
  { title: "Call offs", field: "callOffs" },
];

export const ALL_FRAMEWORK_COLUMNS: ColumnSetting<FrameworkColumns>[] = [
  ...DEFAULT_FRAMEWORK_COLUMNS,
  { title: "Close date", field: "closeDate" },
  { title: "Signals", field: "signals" },
];

const columnCopies: Record<FrameworkColumns, string> = ALL_FRAMEWORK_COLUMNS.reduce(
  (acc, { title, field }) => ({ ...acc, [field]: title }),
  {} as Record<FrameworkColumns, string>,
);

export const getFrameworkColumnCopy = (column?: string): string => {
  if (!column || !(column in columnCopies)) return "";
  return columnCopies[column as FrameworkColumns];
};
